import React, { useEffect, useState } from 'react';
import { apiGet } from '../../../utils/apiUtils';
import './StatisticsPage.scss';
import { RootState } from '../../../../store';
import { useSelector } from 'react-redux';
import CustomSelect from '../../../components/CustomSelect';
import { useI18n } from '../../../contexts/I18nContext';

interface OverallStatistics {
  total_users: number;
  total_chats: number;
  total_protocols: number;
  by_specialty: SpecialtyStatistics[];
}

interface SpecialtyStatistics {
  specialty: string;
  total_users: number;
  total_chats: number;
  total_protocols: number;
  avg_chats_per_user: number;
  avg_protocols_per_user: number;
}

interface UserWithStatistics {
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    specialty: string;
    organization: string;
  };
  statistics: {
    chats_created: number;
    protocols_generated: number;
    last_activity: string | null;
    total_login_count: number;
    user_id: number;
  } | null;
}

const StatisticsPage: React.FC = (): React.ReactElement => {
  const [overallStats, setOverallStats] = useState<OverallStatistics | null>(null);
  const [usersStats, setUsersStats] = useState<UserWithStatistics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('all');
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const user = useSelector((state: RootState) => state.user);
  const { t } = useI18n();


  useEffect(() => {
    fetchOverallStatistics();
    fetchUsersStatistics();
  });

  useEffect(() => {
    fetchUsersStatistics();
  }, [selectedSpecialty, limit, offset]);

  const fetchOverallStatistics = async () => {
    try {
      const organizationParam = user?.role_id === 4 ? 'all' : user?.organization;
      const response = await apiGet(`/statistics/overall?organization=${organizationParam}`);

      if (!response.ok) {
        throw new Error(t('statistics.error.overall'));
      }

      const data = await response.json();
      setOverallStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('statistics.error.unknown'));
    }
  };

  const fetchUsersStatistics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedSpecialty !== 'all') {
        params.append('specialty', selectedSpecialty);
      }
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      const organizationParam = user?.role_id === 4 ? 'all' : user?.organization || '';
      params.append('organization', organizationParam);

      const response = await apiGet(`/statistics/users?${params}`);

      if (!response.ok) {
        throw new Error(t('statistics.error.users'));
      }

      const data = await response.json();
      setUsersStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('statistics.error.unknown'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('statistics.notSpecified');
    return new Date(dateString).toLocaleString('ru-RU');
  };



  if (error) {
    return (
      <div className="page-container">
        <div className="statistics-header">
          <div className="header-left">

            <h1>{t('statistics.title')}</h1>
          </div>
        </div>
        <div className="page-content">
          <div className="error-message">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>{t('statistics.tryAgain')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="statistics-header">
        <div className="header-left">
          <h1>{t('statistics.title')}</h1>
        </div>
      </div>
      <div className="page-content statistics-page">

        {overallStats && (
          <div className="overall-statistics">
            <h2>{t('statistics.overall')}</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">{overallStats.total_users}</div>
                <div className="stat-label">{t('statistics.usersCount')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{overallStats.total_chats}</div>
                <div className="stat-label">{t('statistics.chatsCount')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{overallStats.total_protocols}</div>
                <div className="stat-label">{t('statistics.protocolsCount')}</div>
              </div>
            </div>

            <div className="specialty-statistics">
              <h3>{t('statistics.bySpecialty')}</h3>
              <div className="specialty-grid">
                {overallStats.by_specialty.map((specialty) => (
                  <div key={specialty.specialty} className="specialty-card">
                    <h4>{specialty.specialty}</h4>
                    <div className="specialty-stats">
                      <div className="specialty-stat">
                        <span>{t('statistics.specialty.users')}</span>
                        <span>{specialty.total_users}</span>
                      </div>
                      <div className="specialty-stat">
                        <span>{t('statistics.specialty.chats')}</span>
                        <span>{specialty.total_chats}</span>
                      </div>
                      <div className="specialty-stat">
                        <span>{t('statistics.specialty.protocols')}</span>
                        <span>{specialty.total_protocols}</span>
                      </div>
                      <div className="specialty-stat">
                        <span>{t('statistics.specialty.avgChats')}</span>
                        <span>{specialty.avg_chats_per_user.toFixed(1)}</span>
                      </div>
                      <div className="specialty-stat">
                        <span>{t('statistics.specialty.avgProtocols')}</span>
                        <span>{specialty.avg_protocols_per_user.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="users-statistics">
          <div className="users-header">
            <h2>{t('statistics.usersTitle')}</h2>
            <div className="filters">
              <CustomSelect
                value={selectedSpecialty}
                onChange={(value) => setSelectedSpecialty(value.toString())}
                className=""
                placeholder={t('statistics.filter.allSpecialties')}
                options={[
                  { value: "all", label: t('statistics.filter.allSpecialties') },
                  ...(overallStats?.by_specialty.map((specialty) => ({
                    value: specialty.specialty,
                    label: specialty.specialty
                  })) || [])
                ]}
              />
              <CustomSelect
                value={limit}
                onChange={(value) => setLimit(Number(value))}
                className=""
                placeholder={t('statistics.filter.recordsCount')}
                options={[
                  { value: 10, label: t('admin.filter.records10') },
                  { value: 25, label: t('admin.filter.records25') },
                  { value: 50, label: t('admin.filter.records50') },
                  { value: 100, label: t('admin.filter.records100') }
                ]}
              />
            </div>
          </div>

          {loading ? (
            <div className="loading">{t('statistics.loading')}</div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>{t('statistics.table.user')}</th>
                    <th>{t('statistics.table.email')}</th>
                    <th>{t('statistics.table.specialty')}</th>
                    <th>{t('statistics.table.chatsCreated')}</th>
                    <th>{t('statistics.table.protocolsGenerated')}</th>
                    <th>{t('statistics.table.logins')}</th>
                    <th>{t('statistics.table.lastActivity')}</th>
                  </tr>
                </thead>
                <tbody>
                  {usersStats.map((userStat) => (
                    <tr key={userStat.user.id}>
                      <td>
                        {userStat.user.first_name} {userStat.user.last_name}
                      </td>
                      <td>{userStat.user.email}</td>
                      <td>{userStat.user.specialty}</td>
                      <td>{userStat.statistics?.chats_created || 0}</td>
                      <td>{userStat.statistics?.protocols_generated || 0}</td>
                      <td>{userStat.statistics?.total_login_count || 0}</td>
                      <td>{formatDate(userStat.statistics?.last_activity || null)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="pagination pt-3">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="pagination-btn"
            >
              {t('statistics.pagination.prev')}
            </button>
            <span className="pagination-info">
              {t('statistics.pagination.showingPrefix')} {offset + 1}-{Math.min(offset + limit, offset + usersStats.length)} {t('statistics.pagination.showingSuffix')} {offset + usersStats.length}
            </span>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={usersStats.length < limit}
              className="pagination-btn"
            >
              {t('statistics.pagination.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage; 